import React, { useState, useEffect, useMemo } from 'react';
import { DollarSign, TrendingUp, Calculator, AlertCircle, CheckCircle, Plus, Users, Shield, Zap, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { AZURE } from '../../services/api';
import { BRAND_GREEN, BRAND_GOLD } from '../../theme/sucronTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const PRICING_LABELS = {
  pt: {
    loading: 'Carregando precificação...',
    errorTitle: 'Erro ao carregar precificação',
    retry: 'Tentar novamente',
    pageTitle: 'Precificação VUKZ',
    pageSubtitle: '3 produtos · 8 planos · preços em USD/mês',
    refresh: 'Atualizar',
    mandatoryWarning: 'Gestores obrigatórios:',
    mandatoryDetail: 'Cada plano requer ao menos 1 gestor obrigatório ativo para ser ativado. Planos Governance exigem Gestor Governança. Sports exige Gestor Sports. Full Suite exige ambos (pode ser o mesmo usuário).',
    recommended: 'RECOMENDADO',
    onRequest: 'Sob consulta',
    perMonth: '/ mês',
    requiredLabel: 'Obrigatório para ativar',
    includedLabel: 'Usuários inclusos',
    unlimited: 'ilimitado',
    max: 'máx',
    showLess: 'Mostrar menos',
    moreRoles: (n) => `+${n} papéis`,
    addonsLabel: 'Add-ons disponíveis',
    contactSales: 'Falar com comercial',
    selectPlan: 'Selecionar plano',
    calcTitle: 'Calculadora de Custos',
    calcPlanLabel: 'Plano',
    calcExtraLabel: 'Usuários extras',
    calcSelectPlaceholder: 'Selecione um plano...',
    calcMonthlyTitle: 'Custo estimado / mês',
    calcBase: 'Plano base:',
    calcExtras: 'Extras:',
    calcEmpty: 'Selecione um plano para ver a estimativa.',
    calcActivation: 'Regra de ativação: Todo plano requer ao menos 1 gestor obrigatório ativo. Sem gestor cadastrado, o plano fica pendente.',
    notesTitle: 'Médico e Fisioterapeuta',
    notesText: 'Esses papéis não fazem parte da oferta atual de planos. Quando disponíveis, serão add-ons no produto Sports.',
    planCount: (n) => `${n} plano${n !== 1 ? 's' : ''}`,
  },
  en: {
    loading: 'Loading pricing...',
    errorTitle: 'Error loading pricing',
    retry: 'Try again',
    pageTitle: 'VUKZ Pricing',
    pageSubtitle: '3 products · 8 plans · prices in USD/month',
    refresh: 'Refresh',
    mandatoryWarning: 'Mandatory managers:',
    mandatoryDetail: 'Each plan requires at least 1 active mandatory manager. Governance plans require a Governance Manager. Sports requires a Sports Manager. Full Suite requires both (can be the same user).',
    recommended: 'RECOMMENDED',
    onRequest: 'On request',
    perMonth: '/ mo',
    requiredLabel: 'Required to activate',
    includedLabel: 'Included users',
    unlimited: 'unlimited',
    max: 'max',
    showLess: 'Show less',
    moreRoles: (n) => `+${n} roles`,
    addonsLabel: 'Available add-ons',
    contactSales: 'Talk to sales',
    selectPlan: 'Select plan',
    calcTitle: 'Cost Calculator',
    calcPlanLabel: 'Plan',
    calcExtraLabel: 'Extra users',
    calcSelectPlaceholder: 'Select a plan...',
    calcMonthlyTitle: 'Estimated cost / month',
    calcBase: 'Base plan:',
    calcExtras: 'Extras:',
    calcEmpty: 'Select a plan to see the estimate.',
    calcActivation: 'Activation rule: Every plan requires at least 1 active mandatory manager. Without a registered manager, the plan stays pending.',
    notesTitle: 'Doctor and Physiotherapist',
    notesText: 'These roles are not part of the current plan offering. When available, they will be add-ons in the Sports product.',
    planCount: (n) => `${n} plan${n !== 1 ? 's' : ''}`,
  },
};

// ─── Constantes de estilo ─────────────────────────────────────────────────
const G = BRAND_GREEN || '#064e3b';
const GOLD = BRAND_GOLD || '#b48c2c';

const PRODUCT_META = {
  GOVERNANCE: {
    label: 'Governance',
    subtitle: 'Gestão corporativa, compliance e financeiro',
    color: '#1e40af',
    bg: '#eff6ff',
    border: '#bfdbfe',
    icon: Shield,
  },
  SPORTS: {
    label: 'Sports',
    subtitle: 'Performance esportiva e gestão de atletas',
    color: '#166534',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    icon: Zap,
  },
  FULL_SUITE: {
    label: 'Full Suite',
    subtitle: 'Governance + Sports com desconto',
    color: '#6b21a8',
    bg: '#faf5ff',
    border: '#e9d5ff',
    icon: DollarSign,
  },
};

const TIER_ORDER = { starter: 0, pro: 1, elite: 2, enterprise: 3 };

const TIER_RECOMMENDED = { pro: true, full_suite_pro: true };

// ─── Utilitários ──────────────────────────────────────────────────────────
function buildPlanMatrix(entitlements) {
  // Retorna: { GOVERNANCE: [plan,...], SPORTS: [...], FULL_SUITE: [...] }
  const plans = {};
  entitlements.forEach(e => {
    const product = e.product_code || 'OUTROS';
    const pc = e.plan_code;
    if (!plans[product]) plans[product] = {};
    if (!plans[product][pc]) {
      plans[product][pc] = {
        plan_code: pc,
        plan_name: e.plan_name,
        tier: e.tier,
        price_usd: e.price_usd,
        product_code: product,
        required: [],
        included: [],
        addons: [],
      };
    }
    const p = plans[product][pc];
    if (e.is_required) {
      p.required.push(e);
    } else if (e.is_add_on) {
      p.addons.push(e);
    } else {
      p.included.push(e);
    }
  });

  const result = {};
  Object.entries(plans).forEach(([prod, byPlan]) => {
    result[prod] = Object.values(byPlan)
      .sort((a, b) => (TIER_ORDER[a.tier] ?? 9) - (TIER_ORDER[b.tier] ?? 9));
  });
  return result;
}

// ─── Card de plano ────────────────────────────────────────────────────────
function PlanCard({ plan, productColor, t }) {
  const [open, setOpen] = useState(false);
  const recommended = TIER_RECOMMENDED[plan.plan_code] || plan.tier === 'pro';
  const isEnterprise = plan.tier === 'enterprise';

  return (
    <div style={{
      background: '#fff',
      border: `2px solid ${recommended ? GOLD : '#e5e7eb'}`,
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: recommended ? '0 4px 16px rgba(180,140,44,.15)' : '0 1px 4px rgba(0,0,0,.06)',
      display: 'flex', flexDirection: 'column',
    }}>
      {recommended && (
        <div style={{ background: GOLD, textAlign: 'center', padding: '5px 0', fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: .5 }}>
          {t.recommended}
        </div>
      )}

      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${G} 0%, #047857 100%)`, padding: '18px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#a7f3d0', textTransform: 'uppercase', letterSpacing: .6 }}>
              {plan.tier}
            </p>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: '3px 0 0' }}>
              {plan.plan_name}
            </h3>
          </div>
          <div style={{ textAlign: 'right' }}>
            {isEnterprise ? (
              <p style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{t.onRequest}</p>
            ) : (
              <>
                <p style={{ fontSize: 28, fontWeight: 900, color: '#fff', lineHeight: 1 }}>
                  ${plan.price_usd?.toFixed(0)}
                </p>
                <p style={{ fontSize: 11, color: '#a7f3d0' }}>{t.perMonth}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Gestores obrigatórios */}
      {plan.required.length > 0 && (
        <div style={{ padding: '10px 16px', background: '#fefce8', borderBottom: '1px solid #fde68a' }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 5 }}>
            {t.requiredLabel}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {plan.required.map(e => (
              <span key={e.role_code} style={{
                background: '#fef3c7', color: '#92400e',
                border: '1px solid #fde68a', borderRadius: 5,
                padding: '2px 7px', fontSize: 11, fontWeight: 700,
              }}>
                {e.qty_included}× {e.role_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Usuários inclusos */}
      <div style={{ padding: '12px 16px', flex: 1 }}>
        {plan.included.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
              {t.includedLabel}
            </p>
            {plan.included.slice(0, open ? undefined : 3).map(e => (
              <div key={e.role_code} style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: 12, padding: '3px 0', borderBottom: '1px solid #f9fafb',
              }}>
                <span style={{ color: '#374151', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <CheckCircle size={11} style={{ color: '#16a34a', flexShrink: 0 }} />
                  {e.role_name}
                </span>
                <span style={{ color: '#6b7280', fontSize: 11 }}>
                  {e.qty_included} incl.
                  {e.qty_max === 0 ? ` · ${t.unlimited}` : e.qty_max > 0 ? ` · ${t.max} ${e.qty_max}` : ''}
                  {e.price_per_extra > 0 ? ` · +$${e.price_per_extra}/extra` : ''}
                </span>
              </div>
            ))}
            {plan.included.length > 3 && (
              <button onClick={() => setOpen(o => !o)} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 11, color: G, fontWeight: 600, background: 'none', border: 'none',
                cursor: 'pointer', marginTop: 4, padding: 0,
              }}>
                {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {open ? t.showLess : t.moreRoles(plan.included.length - 3)}
              </button>
            )}
          </div>
        )}

        {plan.addons.length > 0 && (
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
              {t.addonsLabel}
            </p>
            {plan.addons.map(e => (
              <div key={e.role_code} style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: 12, padding: '3px 0', borderBottom: '1px solid #f9fafb',
              }}>
                <span style={{ color: '#374151', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Plus size={11} style={{ color: '#f59e0b', flexShrink: 0 }} />
                  {e.role_name}
                </span>
                <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 600 }}>
                  ${e.price_per_extra}/usuário
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9' }}>
        <button style={{
          width: '100%', padding: '9px 0', borderRadius: 8,
          background: recommended ? G : 'transparent',
          color: recommended ? '#fff' : G,
          border: `1.5px solid ${G}`,
          fontWeight: 700, fontSize: 13, cursor: 'pointer',
          transition: 'all .15s',
        }}>
          {isEnterprise ? t.contactSales : t.selectPlan}
        </button>
      </div>
    </div>
  );
}

// ─── Calculadora ──────────────────────────────────────────────────────────
function CostCalculator({ allPlans, t }) {
  const flatPlans = useMemo(() =>
    Object.values(allPlans).flat().filter(p => p.price_usd > 0),
    [allPlans]
  );
  const [selectedCode, setSelectedCode] = useState('');
  const [extraUsers, setExtraUsers] = useState(0);

  const selected = flatPlans.find(p => p.plan_code === selectedCode);

  const extraCost = useMemo(() => {
    if (!selected) return 0;
    const extraRate = selected.included
      .reduce((sum, e) => sum + (e.price_per_extra || 0), 0);
    return extraUsers * extraRate;
  }, [selected, extraUsers]);

  const total = selected ? (selected.price_usd + extraCost) : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <Calculator className="w-5 h-5" style={{ color: G }} />
        <h3 className="text-lg font-bold" style={{ color: G }}>{t.calcTitle}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.calcPlanLabel}</label>
          <select
            value={selectedCode}
            onChange={e => setSelectedCode(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">{t.calcSelectPlaceholder}</option>
            {['GOVERNANCE', 'SPORTS', 'FULL_SUITE'].map(prod =>
              (allPlans[prod] || []).filter(p => p.price_usd > 0).map(p => (
                <option key={p.plan_code} value={p.plan_code}>
                  {PRODUCT_META[prod]?.label} — {p.plan_name} (${p.price_usd?.toFixed(0)}/mês)
                </option>
              ))
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.calcExtraLabel}</label>
          <input
            type="number" min={0}
            value={extraUsers}
            onChange={e => setExtraUsers(Math.max(0, parseInt(e.target.value) || 0))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
            placeholder="0"
          />
        </div>
      </div>

      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>{t.calcMonthlyTitle}</span>
          <span style={{ fontSize: 30, fontWeight: 900, color: G }}>
            {total != null ? `$${total.toFixed(0)}` : '$--'}
          </span>
        </div>
        {selected && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span>{t.calcBase} <strong>${selected.price_usd?.toFixed(0)}</strong></span>
            {extraCost > 0 && <span>{t.calcExtras} <strong>+${extraCost.toFixed(0)}</strong></span>}
          </div>
        )}
        {!selected && (
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
            {t.calcEmpty}
          </p>
        )}
      </div>

      <div style={{ marginTop: 12, padding: '10px 14px', background: '#fefce8', borderRadius: 8, border: '1px solid #fde68a', fontSize: 12, color: '#92400e' }}>
        <strong>{t.calcActivation.split(':')[0]}:</strong> {t.calcActivation.split(':').slice(1).join(':')}
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────
const GovernancePricingPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [allPlans, setAllPlans] = useState({});

  const { lang } = useGovernanceLang();
  const t = PRICING_LABELS[lang] || PRICING_LABELS.pt;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await AZURE.get('/governance/entitlements');
      const ents = data?.data?.entitlements || [];
      setAllPlans(buildPlanMatrix(ents));
    } catch (err) {
      console.error('[GovernancePricingPage]', err);
      setError(err.response?.data?.error || 'Erro ao carregar precificação');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-gray-200 border-t-green-600 rounded-full mx-auto mb-4" />
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-2">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <h3 className="text-lg font-bold text-red-900">{t.errorTitle}</h3>
        </div>
        <p className="text-red-700 mb-4">{error}</p>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold">
          <RefreshCw size={14} /> {t.retry}
        </button>
      </div>
    );
  }

  const productOrder = ['GOVERNANCE', 'SPORTS', 'FULL_SUITE'];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={e => { e.target.style.display = 'none'; }} />
            <div>
              <h2 className="text-2xl font-extrabold" style={{ color: G }}>{t.pageTitle}</h2>
              <p className="text-gray-500 text-sm">{t.pageSubtitle}</p>
            </div>
          </div>
          <button onClick={load} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
            <RefreshCw size={13} /> {t.refresh}
          </button>
        </div>
      </div>

      {/* Aviso sobre gestores obrigatórios */}
      <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Users size={18} style={{ color: '#92400e', flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 13, color: '#92400e' }}>
        <strong>{t.mandatoryWarning}</strong> {t.mandatoryDetail}
        </div>
      </div>

      {/* Grade de produtos */}
      {productOrder.map(productCode => {
        const plans = allPlans[productCode] || [];
        if (!plans.length) return null;
        const meta = PRODUCT_META[productCode] || PRODUCT_META.GOVERNANCE;
        const Icon = meta.icon;

        return (
          <div key={productCode}>
            {/* Product header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 16,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 9,
                background: meta.bg, border: `1.5px solid ${meta.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={18} style={{ color: meta.color }} />
              </div>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: meta.color, margin: 0 }}>
                  {meta.label}
                </h3>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>{meta.subtitle}</p>
              </div>
              <span style={{
                marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: meta.color,
                background: meta.bg, border: `1px solid ${meta.border}`,
                borderRadius: 6, padding: '3px 10px',
              }}>
                {t.planCount(plans.length)}
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(plans.length, 4)}, minmax(220px, 1fr))`,
              gap: 16,
            }}>
              {plans.map(plan => (
                <PlanCard key={plan.plan_code} plan={plan} productColor={meta.color} t={t} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Calculadora */}
      <CostCalculator allPlans={allPlans} t={t} />

      {/* Notas */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-blue-900 text-sm">{t.notesTitle}</p>
            <p className="text-xs text-blue-700 mt-0.5">{t.notesText}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GovernancePricingPage;
